import { useState } from 'react';
import { CheckCircle2, ChevronDown, CircleAlert, HelpCircle } from 'lucide-react';
import type { GiftHealth } from '@/lib/giftCards';

/**
 * Compact operational readiness block for the clinic team ("מצב מערכת").
 * Shows green/red state per capability plus a short built-in help section.
 * Never renders a secret value, a raw gift-card code or a technical error.
 */
export default function GiftSystemStatus({ health }: { health: GiftHealth | null }) {
  const [helpOpen, setHelpOpen] = useState(false);
  if (!health) return null;

  const items: Array<{ label: string; ok: boolean; hint: string }> = [
    {
      label: 'חיבור לשופיפיי (שוברים)',
      ok: !!health.shopifyGiftCards.read && !!health.shopifyGiftCards.write,
      hint: 'הרשאות הנפקה וקריאה של שוברי מתנה',
    },
    {
      label: 'חיוב וזיכוי ידני',
      ok: !!health.shopifyGiftCards.write_transactions,
      hint: 'הורדת סכום עבור טיפול והחזרה במקרה של תיקון',
    },
    { label: 'הצפנת קודים', ok: !!health.encryption, hint: 'קודי השוברים נשמרים מוצפנים' },
    { label: 'אבטחת עמוד ציבורי', ok: health.pepper !== false, hint: 'הגנה על קישורי /gift ואימות דו-שלבי' },
    { label: 'כתובת האתר', ok: health.publicSiteUrl !== false, hint: 'קישורים אישיים ומיילים' },
    {
      label: 'שליחת מייל',
      ok: !!health.email,
      hint: health.emailSender ? `נשלח מהכתובת ${health.emailSender}` : 'שליחת שוברים וקודי אימות',
    },
    {
      label: 'עדכונים משופיפיי (webhooks)',
      ok: !!health.webhooks?.ok,
      hint: health.webhooks?.checked
        ? health.webhooks.ok
          ? 'תשלום, החזר וביטול הזמנה מתעדכנים אוטומטית'
          : 'חסרים נושאים: ' + (health.webhooks.missing.join(', ') || '—')
        : 'לא ניתן לבדוק כרגע',
    },
    {
      label: 'תחזוקה אוטומטית',
      ok: !!health.automation?.cronConfigured,
      hint: health.automation?.lastSyncAt
        ? `ריצה אחרונה: ${new Date(health.automation.lastSyncAt).toLocaleString('he-IL')}`
        : 'מתוזמן — ממתין לריצה הראשונה',
    },
    {
      label: 'רכישת שוברים באתר',
      ok: !!health.purchase?.publicSaleEnabled && !!health.purchase?.ready,
      hint:
        health.purchase && !health.purchase.publicSaleEnabled
          ? 'כרגע כבוי בהגדרות — לא ניתן לרכוש באתר'
          : health.purchase?.ready
            ? 'פתוח לרכישה עם תשלום מאובטח'
            : 'חסום עד להשלמת ההגדרות',
    },
  ];

  const readyCount = items.filter((i) => i.ok).length;

  return (
    <section className="border rounded-xl bg-white p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">מצב מערכת</h2>
        <div className="text-xs text-muted-foreground">
          {readyCount} מתוך {items.length} מוכנים
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((item) => (
          <div
            key={item.label}
            className={`flex items-start gap-2 rounded-lg border p-2.5 text-sm ${
              item.ok ? 'border-emerald-200 bg-emerald-50/60' : 'border-rose-200 bg-rose-50/60'
            }`}
          >
            {item.ok ? (
              <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-600" />
            ) : (
              <CircleAlert className="h-4 w-4 mt-0.5 shrink-0 text-rose-600" />
            )}
            <div className="min-w-0">
              <div className="font-medium leading-tight">{item.label}</div>
              <div className="text-xs text-muted-foreground mt-0.5 break-words">{item.hint}</div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={() => setHelpOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        איך עובדים עם שוברים?
        <ChevronDown className={`h-3.5 w-3.5 transition ${helpOpen ? 'rotate-180' : ''}`} />
      </button>

      {helpOpen && (
        <ul className="text-xs leading-relaxed text-muted-foreground space-y-1.5 border-t pt-3">
          <li>
            <span className="font-medium text-foreground">שובר מתנה חדש:</span> לשונית „שוברים“ → „שובר חדש“. ממלאים סכום
            ושם, בוחרים שליחה במייל או קישור אישי, ומאשרים.
          </li>
          <li>
            <span className="font-medium text-foreground">חיוב עבור טיפול:</span> פותחים את השובר ברשימה → „חיוב“, מזינים
            את סכום הטיפול. היתרה מתעדכנת בשופיפיי.
          </li>
          <li>
            <span className="font-medium text-foreground">זיכוי או תיקון:</span> פותחים את השובר → „זיכוי“, מציינים סכום
            וסיבה. מתאים לביטול טיפול או טעות בחיוב.
          </li>
          <li>
            <span className="font-medium text-foreground">שליחה חוזרת:</span> פותחים את השובר → „שליחה חוזרת“ למייל, או
            „העתקת קישור“ ושליחה בוואטסאפ. הקוד המלא נחשף רק למקבל/ת אחרי אימות במייל.
          </li>
          <li>
            <span className="font-medium text-foreground">בדיקות ואי-התאמות:</span> לשונית „רכישות מהאתר“ מציגה רכישות
            והתראות שדורשות בדיקה. „סנכרון יתרות“ מרענן מיד; אחרת המערכת מתעדכנת לבדה כל חמש דקות.
          </li>
        </ul>
      )}
    </section>
  );
}
